import React, { useEffect } from 'react';
import { useBILogger } from '@wix/fe-essentials/react';
import {
  TableListItem,
  Box,
  Text,
  Image,
  IconButton,
} from '@wix/design-system';
import { ChevronRight } from '@wix/wix-ui-icons-common';
import { Section } from '../Section/Section';
import { getRecommendedToolDataHook } from '../../../constants/automationIds';
import { RecommendedTool } from '../../../services/recommendedTools/RecommendedTools';
import { TRANSLATIONS } from '../../../constants/translations';
import styles from './RecommendedTools.module.scss';
import { t } from '../../../../../essentials/i18n';
import {
  getAppDefIdFromBusinessAppOrigin,
  getBIReportData,
  isBusinessAppOrigin,
} from '../../../utils/biEvents';
import { BI_EVENTS_IDS, BI_PARAMS } from '../../../constants/biEvents';
import {
  RECOMMENDED_APPS_TAB_ID,
  BUSINESS_APP_ORIGIN,
} from '../../../constants/constants';

interface RecommendedToolsProps {
  tools: RecommendedTool[];
  trigger?: string;
}

export const RecommendedTools: React.FC<RecommendedToolsProps> = ({
  tools,
  trigger,
}) => {
  const logger = useBILogger();

  const onToolClick = (tool: RecommendedTool, index: number) => {
    if (trigger && isBusinessAppOrigin(trigger)) {
      logger.log(
        getBIReportData(
          BI_EVENTS_IDS.ACTION_CLICK,
          getAppDefIdFromBusinessAppOrigin(trigger),
          {
            section: BI_PARAMS.SECTIONS.RECOMMENDED_TOOLS,
            action_id: BI_PARAMS.ACTION_ID.RECOMMENDED_TOOLS,
            target_cta: BI_PARAMS.TARGET_CTA.BUTTON_CLICKED,
            action_rank_on_section: index + 1,
          },
        ),
      );
    }
    tool.executeAction();
  };

  useEffect(() => {
    if (trigger && !isBusinessAppOrigin(trigger)) {
      logger.log(
        getBIReportData(BI_EVENTS_IDS.TAB_LOAD, RECOMMENDED_APPS_TAB_ID, {
          trigger,
        }),
      );
    }
  }, []);

  return (
    <Section heading={t(TRANSLATIONS.MY_BUSINESS.HEADERS.RECOMMENDED_TOOLS)}>
      {tools.map((tool, index) => (
        <TableListItem
          key={tool.metadata.offerId}
          dataHook={getRecommendedToolDataHook(tool.metadata.offerId)}
          className={styles.itemContainer}
          verticalPadding="medium"
          showDivider={index !== tools.length - 1}
          onClick={() => onToolClick(tool, index)}
          options={[
            {
              value: (
                <Image
                  width="48px"
                  height="48px"
                  src={tool.imageSrc}
                  alt={t(tool.name)}
                  transparent
                />
              ),
              width: '48px',
              align: 'left',
            },
            {
              value: (
                <Box direction="vertical">
                  <Text size="small" weight="normal">
                    {t(tool.name)}
                  </Text>
                  <Text
                    size="small"
                    weight="thin"
                    secondary
                    maxLines={2}
                    ellipsis
                  >
                    {t(tool.description)}
                  </Text>
                </Box>
              ),
              width: 'auto',
            },
            {
              value: (
                <IconButton priority="secondary" size="tiny">
                  <ChevronRight />
                </IconButton>
              ),
              align: 'right',
            },
          ]}
        />
      ))}
    </Section>
  );
};
